import { convertTimestamp } from './general-helpers';
import { AnalyticsPartialEvalueeWithAssessmentData, ChildReport, ChildReportMultipleType, EvalueeCompletionMonthDetail, EvalueeCompletionReport, GroupAptitudeReportStat, GroupInterestReportStat, GroupTaskPerformanceReportStat, QuickSelectDate, QuickSelectDateOption, ReportInput } from '../models/src';
import { aptitudes, aptitudeTasks } from './populateAptitudes';
import { interests } from './populateInterests';


export function getQuickSelectDateOption(quickSelectDate: QuickSelectDate): { startDate: Date, endDate: Date } {
  const now = new Date(Date.now());

  switch (quickSelectDate) {
    case 'last-30-days':
    default:  
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30),
        endDate: now
      };
    case 'this-month':
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1),
        endDate: now
      };
    case 'last-month':
      return {
        startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        endDate: new Date(now.getFullYear(), now.getMonth(), 0)
      };
    case 'this-year':
      return {
        startDate: new Date(now.getFullYear(), 0, 1),
        endDate: now
      };
    case 'last-year':
      return {
        startDate: new Date(now.getFullYear() - 1, 0, 1),
        endDate: new Date(now.getFullYear(), 0, 0)
      };
    case 'all-time':
      return {
        startDate: new Date(1609502400000),
        endDate: now
      };
    }
}

export const QuickSelectDateOptions: QuickSelectDateOption[] = [
  {
    value: 'last-30-days',
    name: 'Last 30 Days',
    rerunReport: true
  },
  {
    value: 'this-month',
    name: 'This month',
    rerunReport: true
  },
  {
    value: 'last-month',
    name: 'Last month',
    rerunReport: false
  },
  {
    value: 'this-year',
    name: 'This year',
    rerunReport: true
  },
  {
    value: 'last-year',
    name: 'Last year',
    rerunReport: false
  },
  {
    value: 'all-time',
    name: 'All time',
    rerunReport: true
  },
  {
    value: 'custom',
    name: 'Custom',
    rerunReport: false
  }
];

// If the endDate is after the generatedDate and is custom report, then the report needs to be rerun
export function shouldRerunReport(quickSelectDate: QuickSelectDate, endDate: Date, generatedDate: Date): boolean | undefined {
  if (quickSelectDate === 'custom' && endDate > generatedDate) {
    return true;
  }

  return QuickSelectDateOptions.find(qd => qd.value === quickSelectDate)?.rerunReport
}

export function createReportInputDTO(reportInput: ReportInput, timezoneOffset = 0) {
  const { portalIds, counselors, quickDate, startDate, endDate, tags } = reportInput;

  startDate.setHours(0, 0, 0, 0);
  const start = startDate.getTime();

  endDate.setHours(23, 59, 59, 999);
  const end = endDate.getTime();

  console.log('start', start, 'end', end, 'offset', timezoneOffset);

  return {
    portalIds,
    counselors,
    quickDate,
    startDate: start,
    endDate: end,
    timezoneOffset,
    tags: tags ?? []
  };
}

export const defaultQuickSelectDate: QuickSelectDate = 'last-30-days';
export const defaultCounselors = ['all'];

// This function generates the individual reports that make up the group report ()
export function generateGroupedReports(
  assessments: AnalyticsPartialEvalueeWithAssessmentData[],
  startDate: Date,
  endDate: Date,
  portalIds: string[],
  includeCompletionDetails = true
): ChildReportMultipleType<'group'> {
  const interestReport: ChildReport<'interest'> = {
    reportType: 'interest',
    stats: calculateInterestCategoryStats(assessments),
  };

  const aptitudeReport: ChildReport<'aptitude'> = {
    reportType: 'aptitude',
    stats: calculateAptitudeStats(assessments),
  };

  const taskPerformanceReport: ChildReport<'taskPerformance'> = {
    reportType: 'taskPerformance',
    stats: calculateTaskPerformanceStats(assessments),
  };

  const summary: ChildReport<'summary'> = {
    reportType: 'summary',
    stats: getEvalueeCompletionByMonth(assessments, startDate, endDate, portalIds, includeCompletionDetails),
  };

  return [interestReport, aptitudeReport, taskPerformanceReport, summary];
}

function calculateInterestCategoryStats(assessments: AnalyticsPartialEvalueeWithAssessmentData[]): GroupInterestReportStat[] {
  const stats: GroupInterestReportStat[] = [];

  interests.forEach((interest) => {
    const likeValues: number[] = [];
    const dislikeValues: number[] = [];
    const unansweredValues: number[] = [];
    let topRanked = 0;
    let anyRanked = 0;

    assessments.forEach((evaluee) => {
      const result = evaluee.interestResults.find((r) => r.interestCategory === interest.interestCategory);

      if (result) {
        likeValues.push(result.like);
        dislikeValues.push(result.dislike);
        unansweredValues.push(result.unanswered);
      }

      if (evaluee.topInterestScores) {
        const topInterest = evaluee.topInterestScores.find(top => top.interestCategory === interest.interestCategory);

        if (topInterest) {
          if (topInterest.rank > 0 && topInterest.rank <= 3) {
            topRanked++;
          }
          anyRanked++;
        }
      }
    });

    const categoryStats: GroupInterestReportStat = {
      category: interest.interestCategory,
      name: interest.name,
      topRanked,
      anyRanked,
      like: {
        mean: calculateMean(likeValues),
        stdDev: calculateStdDev(likeValues),
      },
      dislike: {
        mean: calculateMean(dislikeValues),
        stdDev: calculateStdDev(dislikeValues),
      },
      unanswered: {
        mean: calculateMean(unansweredValues),
        stdDev: calculateStdDev(unansweredValues),
      },
    };

    stats.push(categoryStats);
  });

  return stats;
}

function calculateAptitudeStats(assessments: AnalyticsPartialEvalueeWithAssessmentData[]): GroupAptitudeReportStat[] {
  const stats: GroupAptitudeReportStat[] = [];
  aptitudes.forEach((aptitude) => {
    const values: number[] = [];

    assessments.forEach((evaluee) => {
      const result = evaluee.aptitudeScores.find((r) => r.aptitude === aptitude.type);

      if (result) {
        values.push(result.score);
      }
    });

    const categoryStats: GroupAptitudeReportStat = {
      aptitude: aptitude.type,
      name: aptitude.name,
      mean: calculateMean(values),
      stdDev: calculateStdDev(values),
      min: values.length === 0 ? 0 : Math.min(...values),
      max: values.length === 0 ? 0 : Math.max(...values),
    };

    stats.push(categoryStats);
  });

  return stats;
}

function calculateTaskPerformanceStats(assessments: AnalyticsPartialEvalueeWithAssessmentData[]): GroupTaskPerformanceReportStat[] {
  const stats: GroupTaskPerformanceReportStat[] = [];
  aptitudeTasks.forEach((task) => {
    const correctValues: number[] = [];
    const attemptedValues: number[] = [];

    assessments.forEach((evaluee) => {
      const result = evaluee.taskResults.find((r) => r.taskId === task.id);

      if (result) {
        correctValues.push(result.correct || 0);
        attemptedValues.push(task.questions.length - (result.unanswered || 0));
      };
    });

    const taskStats: GroupTaskPerformanceReportStat = {
      task: task.name,
      correct: {
        mean: calculateMean(correctValues),
        stdDev: calculateStdDev(correctValues),
      },
      attempted: {
        mean: calculateMean(attemptedValues),
        stdDev: calculateStdDev(attemptedValues),
      },
    };

    stats.push(taskStats);
  });

  return stats;
}



function calculateMean(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  return +(sum / values.length).toFixed(1);
}

function calculateStdDev(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const mean = calculateMean(values);
  const variance = values.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / values.length;
  return +Math.sqrt(variance).toFixed(1);
}

// TODO: not sure if I like this approach for return type of an object rather than an array, but it works for now
function getEvalueeCompletionByMonth(
  assessments: AnalyticsPartialEvalueeWithAssessmentData[],
  startDate: Date,
  endDate: Date,
  portalIds: string[],
  includeCompletionDetails = true
): EvalueeCompletionReport[] {

  // Create an array of months between the start and end dates
  const months = getMonthsBetween(startDate, endDate);
  const allMonths = months.reduce((acc, month) => {
    acc.push({
      month: month,
      portalIdCounts: {},
      ...(includeCompletionDetails ? { completionsByPortalId: {} } : {})
    });
    return acc;
  }, [] as EvalueeCompletionReport[]);

  assessments
    .filter((assessment) => assessment.completedDate)
    .forEach((assessment) => {
      const date = assessment.completedDate ? convertTimestamp(assessment.completedDate) : new Date(Date.now());
      const month = monthKey(date);

      // Check if the evalueeCompletion object already has an entry for the portalId
      const evalueeReport = allMonths.find((report) => report.month === month) as EvalueeCompletionReport;

      // Increment the count for the portalId
      if (!evalueeReport?.portalIdCounts[assessment.portalId]) {
        evalueeReport.portalIdCounts[assessment.portalId] = 1;
      } else {
        evalueeReport.portalIdCounts[assessment.portalId]++;
      }

      if (includeCompletionDetails) {
        // CSE-104: append per-seeker detail row (fullName + counselorName resolved by CSE-102)
        const byPortal = evalueeReport.completionsByPortalId as { [portalId: string]: EvalueeCompletionMonthDetail[] };
        if (!byPortal[assessment.portalId]) {
          byPortal[assessment.portalId] = [];
        }
        byPortal[assessment.portalId].push({
          uid: assessment.uid,
          fullName: assessment.fullName ?? '',
          counselorName: assessment.counselorName ?? '',
          completedDate: convertTimestamp(assessment.completedDate as Date),
          // CSE-110: carry top-3 FIT recommendations through to stored report (only for fitScore portals)
          ...(assessment.fitRecommendations ? { fitRecommendations: assessment.fitRecommendations } : {})
        });
      }
    });

  // Ensure every portalId has a count entry and an empty detail array for every month.
  allMonths.forEach((evalueeReport) => {
    portalIds.forEach((portalId) => {
      if (!evalueeReport.portalIdCounts[portalId]) {
        evalueeReport.portalIdCounts[portalId] = 0;
      }
      if (includeCompletionDetails) {
        const byPortal = evalueeReport.completionsByPortalId as { [portalId: string]: EvalueeCompletionMonthDetail[] };
        if (!byPortal[portalId]) {
          byPortal[portalId] = [];
        }
      }
    });
  });

  return allMonths;
}

function getMonthsBetween(startDate: Date, endDate: Date): string[] {
  const months: string[] = [];
  const currentDate = new Date(startDate);
 
  while (true) {
    months.push(monthKey(currentDate));
    if (currentDate.getFullYear() === endDate.getFullYear() && currentDate.getMonth() === endDate.getMonth()) {
      break;
    }
    currentDate.setMonth(currentDate.getMonth() + 1);
    currentDate.setDate(1);
  }
  return months;
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
}